/**
 * A phone block-editing session: the second undo stack.
 *
 * On a phone, opening a block's full-screen editor starts a per-block history. Edits go here rather
 * than onto the task stack, and closing collapses the whole visit into exactly ONE `update-block`
 * op — a phone editing session is one act, and re-opening the full-screen editor on every undo
 * click would be noise.
 *
 * Extracted from `task-workspace-page` as pure transitions because it was the one part of undo with
 * no coverage, and both defects an audit found lived here: other handlers walked past the ref
 * because nothing told them it existed. A reducer cannot be walked past silently — every caller has
 * to say what it is doing to the session, or leave it alone deliberately.
 *
 * Holds no React state and touches no DOM. The caller applies the returned data to blocks.
 */

export interface PhoneSession {
  blockId: string;
  /** The block's data when the session opened — what a single committed op is measured against. */
  prevData: Record<string, unknown>;
  history: Record<string, unknown>[];
  historyIdx: number;
  /**
   * OTHER blocks this session changed, as they were BEFORE it touched them. Captured once each,
   * the first time, so the value here is always the pre-session state however many times the
   * block moves afterwards.
   *
   * A session is one block's editor, and for almost everything that is the whole story. Deleting
   * a leader's variant is not: it takes the matching answer row out of every follower (§7.3), so
   * the visit changed blocks whose editors were never open. Without this the committed op
   * described the leader alone, and one undo put the variant back with its answers still gone.
   *
   * Not in `history`: the in-editor Undo steps through what the teacher did IN this editor, and
   * silently reverting another block under it is not that. These are settled once, on close.
   */
  others?: Record<string, Record<string, unknown>>;
}

export const canUndo = (s: PhoneSession | null): boolean => !!s && s.historyIdx > 0;
export const canRedo = (s: PhoneSession | null): boolean => !!s && s.historyIdx < s.history.length - 1;

/** Whether this session is about that block — the guard every other handler needs. */
export const sessionCovers = (s: PhoneSession | null, blockId: string): boolean =>
  !!s && s.blockId === blockId;

export function openSession(blockId: string, prevData: Record<string, unknown>): PhoneSession {
  return { blockId, prevData, history: [prevData], historyIdx: 0 };
}

/**
 * An edit inside the session.
 *
 * Truncates anything ahead of the cursor, so editing after undoing discards the redo branch — the
 * same rule the task stack follows.
 */
export function recordEdit(session: PhoneSession, data: Record<string, unknown>): PhoneSession {
  const history = [...session.history.slice(0, session.historyIdx + 1), data];
  return { ...session, history, historyIdx: history.length - 1 };
}

/**
 * Remember what another block looked like before this session changed it.
 *
 * First write wins — the point is the state the session INHERITED, so a block edited three times
 * still reverts to where it started rather than to its second-last value.
 */
export function recordOther(
  session: PhoneSession, blockId: string, prevData: Record<string, unknown>,
): PhoneSession {
  if (session.others?.[blockId] !== undefined) return session;
  return { ...session, others: { ...session.others, [blockId]: prevData } };
}

/** Step back. Returns the session and the data to apply, or null when there is nowhere to go. */
export function stepBack(session: PhoneSession): { session: PhoneSession; data: Record<string, unknown> } | null {
  if (!canUndo(session)) return null;
  const historyIdx = session.historyIdx - 1;
  return { session: { ...session, historyIdx }, data: session.history[historyIdx]! };
}

export function stepForward(session: PhoneSession): { session: PhoneSession; data: Record<string, unknown> } | null {
  if (!canRedo(session)) return null;
  const historyIdx = session.historyIdx + 1;
  return { session: { ...session, historyIdx }, data: session.history[historyIdx]! };
}

/**
 * What closing the session should commit, given the block's data now.
 *
 * `null` for "nothing happened" — an unchanged session, or one whose block no longer exists.
 *
 * That second case is not hypothetical: delete is reachable from inside the phone editor, and
 * nothing used to clear the session when it fired. Closing then measured a missing block's data
 * (`{}`) against `prevData`, committed an `update-block` op for a block that was gone, and left an
 * op that `applyOp` silently no-ops over — undo advanced the stack and nothing moved.
 */
export function closeSession(
  session: PhoneSession | null,
  currentData: Record<string, unknown> | undefined,
  /** Every other block's data NOW, for the ones this session touched. */
  currentOthers?: (blockId: string) => Record<string, unknown> | undefined,
): {
  blockId: string; prev: Record<string, unknown>; next: Record<string, unknown>;
  /** Changed siblings, already filtered to the ones that really moved. Empty for most visits. */
  others: { blockId: string; prev: Record<string, unknown>; next: Record<string, unknown> }[];
} | null {
  if (!session) return null;
  if (currentData === undefined) return null; // the block was deleted during the session
  const others = Object.entries(session.others ?? {}).flatMap(([blockId, prev]) => {
    const next = currentOthers?.(blockId);
    // Gone, or back where it started: nothing to commit either way.
    if (next === undefined || JSON.stringify(next) === JSON.stringify(prev)) return [];
    return [{ blockId, prev, next }];
  });
  // The SIBLINGS alone can be the whole change — a variant delete leaves the leader's own data
  // altered too, but checking only this block would have dropped the op if it ever did not.
  if (JSON.stringify(currentData) === JSON.stringify(session.prevData) && others.length === 0) return null;
  return { blockId: session.blockId, prev: session.prevData, next: currentData, others };
}
